/**
 * Filters items by search text in title and description
 * AND semantics across terms; exact/near-exact matches are prioritized first.
 *
 * @param {Array} itemsToFilter - Array of items to filter
 * @param {string} searchText - Text to search for
 * @returns {Array} Filtered items that match all terms, ordered by relevance
 */
const filterBySearchText = (itemsToFilter, searchText) => {
  if (!searchText || searchText.trim().length < 2) return itemsToFilter;

  const normalized = searchText.toLowerCase().trim();
  const terms = normalized
    .split(/\s+/)
    .map((term) => term.trim())
    .filter((term) => term.length >= 2);

  if (terms.length === 0) return itemsToFilter;

  const { exactMatches, partialMatches } = itemsToFilter.reduce(
    (acc, item) => {
      const title = (item.title || "").toLowerCase();
      const description = (item.description || "").toLowerCase();

      const hasAllTerms = terms.every(
        (term) => title.includes(term) || description.includes(term),
      );

      if (!hasAllTerms) {
        return acc;
      }

      const isExact =
        title.includes(normalized) || description.includes(normalized);

      if (isExact) {
        acc.exactMatches.push(item);
      } else {
        acc.partialMatches.push(item);
      }

      return acc;
    },
    { exactMatches: [], partialMatches: [] },
  );

  return [...exactMatches, ...partialMatches];
};

/**
 * Filters items by a specific category ID
 * @param {Array} itemsToFilter - Array of items to filter
 * @param {number} categoryId - Category ID to filter by
 * @returns {Array} Filtered items that match the category ID
 */
const filterByCategory = (itemsToFilter, categoryId) => {
  return itemsToFilter.filter((item) => item.trainingCategoryId === categoryId);
};

/**
 * Filters items by multiple categories
 * AND semantics: when multiple category filters are selected, an item must
 * contain ALL selected category itemIds to be included.
 *
 * @param {Array} itemsToFilter - Array of items to filter
 * @param {Object} categories - Object containing category filter values
 * @param {number} categories.category1 - First category filter ID
 * @param {number} categories.category2 - Second category filter ID
 * @param {number} categories.category3 - Third category filter ID
 * @param {number} categories.category4 - Fourth category filter ID
 * @param {number} categories.category5 - Fifth category filter ID
 * @returns {Array} Filtered items that match all active category filters
 */
const filterByCategories = (itemsToFilter, categories) => {
  if (!categories) return itemsToFilter;

  // Normalize selections per group: OR within a group, AND across groups
  const groups = [];
  for (let i = 1; i <= 5; i++) {
    const key = `category${i}`;
    const value = categories[key];
    const selectedInGroup = [];

    if (Array.isArray(value)) {
      selectedInGroup.push(
        ...value.filter((v) => typeof v === "number" && v !== 0),
      );
    } else if (typeof value === "number" && value !== 0) {
      selectedInGroup.push(value);
    }

    if (selectedInGroup.length > 0) {
      groups.push(selectedInGroup);
    }
  }

  if (groups.length === 0) return itemsToFilter;

  return itemsToFilter.filter((item) => {
    if (Array.isArray(item.categories)) {
      // OR semantics across category groups: item must match ANY of the active groups
      return groups.some((groupIds) =>
        item.categories.some((c) => groupIds.includes(c.itemId)),
      );
    }

    if (typeof item.trainingCategoryId === "number") {
      // Legacy: only match if exactly one group is active and it contains this id
      return groups.length === 1 && groups[0].includes(item.trainingCategoryId);
    }

    return false;
  });
};

/**
 * Filters items by start date - returns items with sessions starting after the specified date
 * @param {Array} itemsToFilter - Array of items to filter
 * @param {string} startDate - Start date in ISO format (YYYY-MM-DD)
 * @returns {Array} Filtered items that have sessions starting after the specified date
 */
const filterByStartDate = (itemsToFilter, startDate) => {
  if (!startDate) return itemsToFilter;
  const filterDate = new Date(startDate);
  filterDate.setHours(0, 0, 0, 0); // Set to start of day for comparison

  return itemsToFilter.filter((item) => {
    if (!item.sessions || !Array.isArray(item.sessions)) {
      return false;
    }

    return item.sessions.some((session) => {
      if (!session.startDate) return false;
      const sessionStartDate = new Date(session.startDate);
      sessionStartDate.setHours(0, 0, 0, 0); // Set to start of day for comparison
      return sessionStartDate >= filterDate;
    });
  });
};

/**
 * Filters items by end date - returns items with sessions ending before the specified date
 * @param {Array} itemsToFilter - Array of items to filter
 * @param {string} endDate - End date in ISO format (YYYY-MM-DD)
 * @returns {Array} Filtered items that have sessions ending before the specified date
 */
const filterByEndDate = (itemsToFilter, endDate) => {
  if (!endDate) return itemsToFilter;

  const filterDate = new Date(endDate);
  filterDate.setHours(23, 59, 59, 999); // Set to end of day for comparison

  return itemsToFilter.filter((item) => {
    if (!item.sessions || !Array.isArray(item.sessions)) {
      return false;
    }

    return item.sessions.some((session) => {
      if (!session.endDate) return false;
      const sessionEndDate = new Date(session.endDate);
      sessionEndDate.setHours(23, 59, 59, 999); // Set to end of day for comparison
      return sessionEndDate <= filterDate;
    });
  });
};

/**
 * Filters items by session region
 * @param {Array} itemsToFilter - Array of items to filter
 * @param {string} region - Region to filter by
 * @returns {Array} Filtered items that have sessions in the specified region
 */
const filterBySessionRegion = (itemsToFilter, region) => {
  if (!region || (Array.isArray(region) && region.length === 0))
    return itemsToFilter;
  const allowed = Array.isArray(region) ? new Set(region) : null;
  return itemsToFilter.filter((item) => {
    if (!item.sessions || !Array.isArray(item.sessions)) return false;
    return item.sessions.some((session) => {
      if (!session.region) return false;
      return allowed ? allowed.has(session.region) : session.region === region;
    });
  });
};

/**
 * Filters items by session city
 * @param {Array} itemsToFilter - Array of items to filter
 * @param {string} city - City to filter by
 * @returns {Array} Filtered items that have sessions in the specified city
 */
const filterBySessionCity = (itemsToFilter, city) => {
  if (!city || (Array.isArray(city) && city.length === 0)) return itemsToFilter;
  const allowed = Array.isArray(city) ? new Set(city) : null;
  return itemsToFilter.filter((item) => {
    if (!item.sessions || !Array.isArray(item.sessions)) return false;
    return item.sessions.some((session) => {
      if (!session.city) return false;
      return allowed ? allowed.has(session.city) : session.city === city;
    });
  });
};

/**
 * Filter strategies object containing different filtering methods
 */
const strategies = {
  search: {
    filter: (items, searchText) => {
      if (!searchText || searchText.length < 2) return items;
      return filterBySearchText(items, searchText);
    },
  },
  category: {
    filter: (items, categoryId) => {
      if (!categoryId) return items;
      return filterByCategory(items, categoryId);
    },
  },
  categories: {
    filter: (items, categories) => {
      if (!categories) return items;
      return filterByCategories(items, categories);
    },
  },
  startDate: {
    filter: (items, startDate) => {
      if (!startDate) return items;
      return filterByStartDate(items, startDate);
    },
  },
  endDate: {
    filter: (items, endDate) => {
      if (!endDate) return items;
      return filterByEndDate(items, endDate);
    },
  },
  sessionRegion: {
    filter: (items, region) => {
      if (!region) return items;
      return filterBySessionRegion(items, region);
    },
  },
  sessionCity: {
    filter: (items, city) => {
      if (!city) return items;
      return filterBySessionCity(items, city);
    },
  },
};

/**
 * Applies multiple filters to a list of items using the strategy pattern
 * @param {Array} items - Array of items to filter
 * @param {Object} activeFilters - Object containing active filters to apply
 * @returns {Array} Items after applying all active filters
 */
const applyFilters = (items, activeFilters) => {
  return Object.entries(activeFilters).reduce(
    (filteredItems, [filterName, filterValue]) => {
      const strategy = strategies[filterName];
      return strategy
        ? strategy.filter(filteredItems, filterValue)
        : filteredItems;
    },
    items,
  );
};

/**
 * Filter service object containing all filtering methods
 */
export const filterService = {
  applyFilters,
  filterBySearchText,
  filterByCategory,
  filterByCategories,
  filterByStartDate,
  filterByEndDate,
  filterBySessionRegion,
  filterBySessionCity,
};
